import { isPublicAuthPath } from '@/utils/public-auth-path';
import { isPathOnlySettingUrl } from '@/utils/settings-validation';

const MAX_DECODE_ITERATIONS = 5;

function normalizeInternalLoginRedirect(value: string): string | null {
  let decoded = value;
  for (let iteration = 0; iteration < MAX_DECODE_ITERATIONS; iteration += 1) {
    if (!isPathOnlySettingUrl(decoded) || !decoded.startsWith('/')) {
      return null;
    }

    if (/%(?![0-9a-f]{2})/i.test(decoded)) {
      return null;
    }

    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      return null;
    }

    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  let url: URL;
  try {
    url = new URL(decoded, 'http://localhost');
  } catch {
    return null;
  }

  if (isPublicAuthPath(url.pathname)) {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

/** Strictly validate a same-origin, root-relative post-login destination. */
export function isSafeInternalLoginRedirect(value: unknown): value is string {
  if (typeof value !== 'string' || !value || value.length > 1024) {
    return false;
  }
  return normalizeInternalLoginRedirect(value) !== null;
}

/**
 * Read and consume the current login URL's redirect exactly once. The query
 * parameter is removed before navigation so a failed assignment cannot replay it.
 */
export function consumeInternalLoginRedirect(fallback = '/'): string {
  if (typeof window === 'undefined') {
    return fallback || '/';
  }

  const currentUrl = new URL(window.location.href);
  const candidate = currentUrl.searchParams.get('redirect');
  currentUrl.searchParams.delete('redirect');
  window.history.replaceState(window.history.state, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);

  return normalizeInternalLoginRedirect(candidate || '') || normalizeInternalLoginRedirect(fallback || '/') || '/';
}

export function reloadAtConsumedInternalLoginRedirect(fallback = '/'): void {
  if (typeof window === 'undefined') {
    return;
  }

  const target = consumeInternalLoginRedirect(fallback);
  window.history.replaceState(window.history.state, '', target);
  window.location.reload();
}
